/* Rinashi cart drawer — warranty add-on toggle.
 *
 * A single opt-in checkbox in the drawer footer. Off by default: checking it
 * adds the $5 warranty line, unchecking removes it. After either, the drawer
 * items + footer are re-rendered from the server (mirroring cart.js's
 * onCartUpdate), so the estimated total and the checkbox's own checked state
 * always reflect the real cart. Event-delegated because the footer is replaced
 * on every cart update.
 */
if (!customElements.get('cart-warranty')) {
  customElements.define('cart-warranty', class extends HTMLElement {});
}

(() => {
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const addUrl = () => (window.routes && window.routes.cart_add_url) || '/cart/add';
  const changeUrl = () => (window.routes && window.routes.cart_change_url) || '/cart/change';

  const postConfig = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let busy = false;

  const updateCart = async (url, body) => {
    const res = await fetch(url, postConfig(body));
    if (!res.ok) {
      let message = `Cart update failed (${res.status})`;
      try {
        const payload = await res.json();
        message = payload.description || payload.message || message;
      } catch (e) {
        /* Keep the status-based fallback. */
      }
      throw new Error(message);
    }
  };

  const refreshDrawer = async () => {
    const res = await fetch(`${cartUrl()}?section_id=cart-drawer`);
    if (!res.ok) throw new Error(`Drawer refresh failed (${res.status})`);

    const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    ['cart-drawer-items', '.drawer__footer'].forEach((selector) => {
      const target = document.querySelector(selector);
      const source = html.querySelector(selector);
      if (!target || !source) throw new Error(`Drawer refresh missing ${selector}`);
      target.replaceWith(source);
    });

    // Header cart bubble.
    try {
      const bubbleRes = await fetch(`${cartUrl()}?section_id=cart-icon-bubble`);
      const bubbleHtml = new DOMParser().parseFromString(await bubbleRes.text(), 'text/html');
      const source = bubbleHtml.querySelector('.shopify-section') || bubbleHtml.body;
      const target = document.getElementById('cart-icon-bubble');
      if (target && source) target.innerHTML = source.innerHTML;
    } catch (e) {
      /* non-fatal */
    }
  };

  const findLineKey = async (variantId) => {
    const res = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    const match = (cart.items || []).find((item) => String(item.variant_id) === String(variantId));
    return match ? match.key : null;
  };

  document.addEventListener('change', async (event) => {
    const check = event.target.closest('[data-cart-warranty]');
    if (!check) return;

    const host = check.closest('cart-warranty');
    const variantId = host && host.dataset.variantId;
    if (!variantId || busy) return;

    busy = true;
    check.disabled = true;
    const wantOn = check.checked;

    try {
      if (wantOn) {
        await updateCart(addUrl(), { items: [{ id: Number(variantId), quantity: 1 }] });
      } else {
        const key = await findLineKey(variantId);
        if (key) await updateCart(changeUrl(), { id: key, quantity: 0 });
      }
      await refreshDrawer();
    } catch (e) {
      console.error('[cart-warranty] toggle failed', e);
      check.checked = !wantOn;
    } finally {
      if (check.isConnected) check.disabled = false;
      busy = false;
    }
  });
})();
